export type CountryOption = {
  code: string;
  label: string;
  regionLabel: string;
  postalLabel: string;
  regionMode: 'us_states' | 'ca_provinces' | 'au_states' | 'uk_countries' | 'free_text';
};

export type RegionOption = {
  code: string;
  label: string;
};

/** Supported website countries (matches existing currency mapping). */
export const LISTING_COUNTRIES: CountryOption[] = [
  {
    code: 'US',
    label: 'United States',
    regionLabel: 'State',
    postalLabel: 'ZIP code',
    regionMode: 'us_states',
  },
  {
    code: 'CA',
    label: 'Canada',
    regionLabel: 'Province / territory',
    postalLabel: 'Postal code',
    regionMode: 'ca_provinces',
  },
  {
    code: 'UK',
    label: 'United Kingdom',
    regionLabel: 'Country / region',
    postalLabel: 'Postcode',
    regionMode: 'uk_countries',
  },
  {
    code: 'AU',
    label: 'Australia',
    regionLabel: 'State / territory',
    postalLabel: 'Postcode',
    regionMode: 'au_states',
  },
];

/** 50 states + DC + supported U.S. territories. Labels prevent AS/AZ confusion. */
export const US_STATES: RegionOption[] = [
  { code: 'AL', label: 'Alabama' },
  { code: 'AK', label: 'Alaska' },
  { code: 'AZ', label: 'Arizona' },
  { code: 'AR', label: 'Arkansas' },
  { code: 'CA', label: 'California' },
  { code: 'CO', label: 'Colorado' },
  { code: 'CT', label: 'Connecticut' },
  { code: 'DE', label: 'Delaware' },
  { code: 'DC', label: 'District of Columbia' },
  { code: 'FL', label: 'Florida' },
  { code: 'GA', label: 'Georgia' },
  { code: 'HI', label: 'Hawaii' },
  { code: 'ID', label: 'Idaho' },
  { code: 'IL', label: 'Illinois' },
  { code: 'IN', label: 'Indiana' },
  { code: 'IA', label: 'Iowa' },
  { code: 'KS', label: 'Kansas' },
  { code: 'KY', label: 'Kentucky' },
  { code: 'LA', label: 'Louisiana' },
  { code: 'ME', label: 'Maine' },
  { code: 'MD', label: 'Maryland' },
  { code: 'MA', label: 'Massachusetts' },
  { code: 'MI', label: 'Michigan' },
  { code: 'MN', label: 'Minnesota' },
  { code: 'MS', label: 'Mississippi' },
  { code: 'MO', label: 'Missouri' },
  { code: 'MT', label: 'Montana' },
  { code: 'NE', label: 'Nebraska' },
  { code: 'NV', label: 'Nevada' },
  { code: 'NH', label: 'New Hampshire' },
  { code: 'NJ', label: 'New Jersey' },
  { code: 'NM', label: 'New Mexico' },
  { code: 'NY', label: 'New York' },
  { code: 'NC', label: 'North Carolina' },
  { code: 'ND', label: 'North Dakota' },
  { code: 'OH', label: 'Ohio' },
  { code: 'OK', label: 'Oklahoma' },
  { code: 'OR', label: 'Oregon' },
  { code: 'PA', label: 'Pennsylvania' },
  { code: 'RI', label: 'Rhode Island' },
  { code: 'SC', label: 'South Carolina' },
  { code: 'SD', label: 'South Dakota' },
  { code: 'TN', label: 'Tennessee' },
  { code: 'TX', label: 'Texas' },
  { code: 'UT', label: 'Utah' },
  { code: 'VT', label: 'Vermont' },
  { code: 'VA', label: 'Virginia' },
  { code: 'WA', label: 'Washington' },
  { code: 'WV', label: 'West Virginia' },
  { code: 'WI', label: 'Wisconsin' },
  { code: 'WY', label: 'Wyoming' },
  { code: 'AS', label: 'American Samoa' },
  { code: 'GU', label: 'Guam' },
  { code: 'MP', label: 'Northern Mariana Islands' },
  { code: 'PR', label: 'Puerto Rico' },
  { code: 'VI', label: 'U.S. Virgin Islands' },
];

export const CA_PROVINCES: RegionOption[] = [
  { code: 'AB', label: 'Alberta' },
  { code: 'BC', label: 'British Columbia' },
  { code: 'MB', label: 'Manitoba' },
  { code: 'NB', label: 'New Brunswick' },
  { code: 'NL', label: 'Newfoundland and Labrador' },
  { code: 'NS', label: 'Nova Scotia' },
  { code: 'NT', label: 'Northwest Territories' },
  { code: 'NU', label: 'Nunavut' },
  { code: 'ON', label: 'Ontario' },
  { code: 'PE', label: 'Prince Edward Island' },
  { code: 'QC', label: 'Quebec' },
  { code: 'SK', label: 'Saskatchewan' },
  { code: 'YT', label: 'Yukon' },
];

export const AU_STATES: RegionOption[] = [
  { code: 'ACT', label: 'Australian Capital Territory' },
  { code: 'NSW', label: 'New South Wales' },
  { code: 'NT', label: 'Northern Territory' },
  { code: 'QLD', label: 'Queensland' },
  { code: 'SA', label: 'South Australia' },
  { code: 'TAS', label: 'Tasmania' },
  { code: 'VIC', label: 'Victoria' },
  { code: 'WA', label: 'Western Australia' },
];

export const UK_COUNTRIES: RegionOption[] = [
  { code: 'England', label: 'England' },
  { code: 'Scotland', label: 'Scotland' },
  { code: 'Wales', label: 'Wales' },
  { code: 'Northern Ireland', label: 'Northern Ireland' },
];

export function getCountryOption(countryCode: string | null | undefined): CountryOption {
  const code = normalizeCountryCode(countryCode);
  return LISTING_COUNTRIES.find((c) => c.code === code) ?? LISTING_COUNTRIES[0];
}

export function normalizeCountryCode(country: string | null | undefined): string {
  if (!country) return 'US';
  const n = country.trim().toUpperCase();
  if (['US', 'USA', 'UNITED STATES'].includes(n)) return 'US';
  if (['CA', 'CANADA'].includes(n)) return 'CA';
  if (['UK', 'GB', 'UNITED KINGDOM', 'GREAT BRITAIN'].includes(n)) return 'UK';
  if (['AU', 'AUSTRALIA'].includes(n)) return 'AU';
  return n.slice(0, 8) || 'US';
}

export function getRegionOptions(countryCode: string): RegionOption[] | null {
  const code = normalizeCountryCode(countryCode);
  const country = LISTING_COUNTRIES.find((c) => c.code === code);
  if (!country) return null;
  switch (country.regionMode) {
    case 'us_states':
      return US_STATES;
    case 'ca_provinces':
      return CA_PROVINCES;
    case 'au_states':
      return AU_STATES;
    case 'uk_countries':
      return UK_COUNTRIES;
    default:
      return null;
  }
}

/**
 * Normalize a stored region value into the canonical code/label for the country.
 * Free-text countries return the trimmed original.
 */
export function normalizeRegionValue(
  countryCode: string,
  region: string | null | undefined
): string {
  const trimmed = (region ?? '').trim();
  if (!trimmed) return '';

  const options = getRegionOptions(countryCode);
  if (!options) return trimmed;

  const upper = trimmed.toUpperCase();
  const byCode = options.find((o) => o.code.toUpperCase() === upper);
  if (byCode) return byCode.code;

  const byLabel = options.find((o) => o.label.toUpperCase() === upper);
  if (byLabel) return byLabel.code;

  // Partial label match (e.g. "Arizona")
  const partial = options.find((o) => o.label.toUpperCase().startsWith(upper));
  if (partial && upper.length >= 3) return partial.code;

  return trimmed;
}

export function isValidRegionForCountry(countryCode: string, region: string): boolean {
  const trimmed = region.trim();
  if (!trimmed) return false;
  const options = getRegionOptions(countryCode);
  if (!options) return true;
  const normalized = normalizeRegionValue(countryCode, trimmed);
  return options.some((o) => o.code === normalized);
}

export function buildGeocodeQuery(parts: {
  address: string;
  city: string;
  state: string;
  zip: string;
  country: string;
}): string {
  const country = getCountryOption(parts.country);
  return [parts.address, parts.city, parts.state, parts.zip, country.label]
    .map((p) => p.trim())
    .filter(Boolean)
    .join(', ');
}
